export const superviseurGuide = {
  id: 'superviseur',
  title: 'Guide Superviseur',
  subtitle: 'Suivi des équipes, gestion des alertes, validation des pointages et bonnes pratiques terrain.',
  sections: [
    /* ============================================================
       1. VOTRE TABLEAU DE BORD
    ============================================================ */
    {
      id: 'votre-dashboard',
      title: 'Votre tableau de bord',
      overview: 'Votre vue quotidienne sur l\'activité de vos équipes : présence, alertes et pointages.',
      content: [
        {
          id: 'accueil-superviseur',
          title: 'Accueil superviseur',
          description: 'En tant que superviseur, votre tableau de bord affiche les donnees filtrees par votre perimetre (site et zone assignes). Vous voyez uniquement les employés de vos équipes.',
          whenToUse: 'Consultez votre tableau de bord chaque matin en arrivant pour avoir une vision claire de votre équipe.',
          steps: [
            'Connectez-vous avec vos identifiants superviseur.',
            'Le tableau de bord s\'affiche automatiquement avec les donnees de votre perimetre.',
            'Les KPIs montrent : Présents, Absents, Attendus, Retards et En pause pour vos équipes uniquement.',
            'La section "Actions prioritaires" affiche les alertes nécessitant votre attention immediate.',
            'Le tableau de présence liste vos employés avec leur statut en temps reel.'
          ],
          screenshot: '/docs/dashboard-supervisor.png',
          screenshotAlt: 'Tableau de bord vue superviseur',
          result: 'Vous savez immédiatement qui est présent, absent ou en retard dans votre équipe.',
          tips: [
            'Les KPIs se mettent a jour en temps reel a chaque pointage.',
            'Cliquez sur "Rafraichir" si vous attendez un pointage recent.'
          ]
        },
        {
          id: 'lire-kpis',
          title: 'Lire les KPIs',
          description: 'Les indicateurs cles vous donnent une synthese instantanee de votre équipe.',
          whenToUse: 'Pour evaluer rapidement la situation du jour.',
          steps: [
            'Présents (vert) : employés ayant pointe a l\'entree et actuellement sur site.',
            'Absents (rouge) : employés planifies mais n\'ayant pas encore pointe.',
            'Attendus (gris) : nombre total d\'employés planifies pour aujourd\'hui.',
            'Retards (orange) : employés ayant pointe apres l\'heure prevue de leur quart.',
            'En pause (bleu) : employés actuellement en pause.'
          ],
          result: 'Vous comprenez en un coup d\'oeil la situation de votre équipe.',
          tips: ['Si "Absents" est élevé le matin, attendez 15 minutes apres le debut du quart : certains employés peuvent etre en retard mais pas encore absents.']
        }
      ]
    },

    /* ============================================================
       2. SUIVI EN TEMPS REEL
    ============================================================ */
    {
      id: 'suivi-temps-reel',
      title: 'Suivi en temps reel',
      overview: 'Suivez l\'activité de vos employés minute par minute : arrivees, departs, pauses.',
      content: [
        {
          id: 'tableau-présence',
          title: 'Tableau de présence',
          description: 'Le tableau de présence affiche chaque employé de votre perimetre avec son statut actuel, colore selon son état : vert (présent), orange (pause), gris (sorti) ou vide (pas encore pointe).',
          whenToUse: 'Pendant la journee de travail pour savoir qui est sur site et ou.',
          steps: [
            'Sur le tableau de bord, le tableau de présence est affiche dans la section principale.',
            'Chaque ligne montre : code employé, nom, zone, quart prevu, heure d\'arrivee, statut.',
            'Vert = L\'employé est actuellement sur site.',
            'Orange = L\'employé est en pause.',
            'Gris = L\'employé a pointe sa sortie.',
            'Vide / Rouge = L\'employé n\'a pas encore pointe (absent ou en retard).',
            'Cliquez sur un employé pour voir sa fiche et son historique de pointage du jour.'
          ],
          screenshot: '/docs/dashboard-présence-table.png',
          screenshotAlt: 'Tableau de présence en temps reel',
          result: 'Vous visualisez en temps reel qui est présent, absent, en pause ou sorti.',
          tips: ['Utilisez la barre de recherche pour trouver rapidement un employé par nom ou code.']
        },
        {
          id: 'derniers-pointages',
          title: 'Derniers pointages',
          description: 'La section "Derniers pointages" montre les evenements de pointage les plus recents avec des icones colorees par type.',
          whenToUse: 'Pour suivre l\'activité en temps reel : qui vient d\'arriver, de sortir ou de prendre une pause.',
          steps: [
            'Descendez sur le tableau de bord pour voir la section "Derniers pointages".',
            'Chaque evenement montre : icone, nom de l\'employé, type (entree, sortie, debut/fin pause), heure.',
            'Fleche verte vers la droite = Entree.',
            'Fleche rouge vers la gauche = Sortie.',
            'Icone cafe = Debut de pause.',
            'Icone retour = Fin de pause.'
          ],
          result: 'Vous suivez le flux d\'activité en temps reel.'
        }
      ]
    },

    /* ============================================================
       3. GESTION DES ALERTES
    ============================================================ */
    {
      id: 'gestion-alertes',
      title: 'Gestion des alertes',
      overview: 'Traitez les retards, absences et anomalies détectés automatiquement par le systeme.',
      content: [
        {
          id: 'alertes-dashboard',
          title: 'Alertes sur le tableau de bord',
          description: 'Les alertes apparaissent dans la section "Actions prioritaires" de votre tableau de bord. Elles signalent les situations nécessitant votre attention immediate.',
          whenToUse: 'Dès que des alertes apparaissent sur votre tableau de bord.',
          steps: [
            'Les alertes sont classees par type : Retard (orange), Absence (rouge), Anomalie (ambre).',
            'Chaque alerte montre : employé concerne, type d\'alerte, heure et détails.',
            'Cliquez sur une alerte pour la traiter directement.',
            'Sélectionnez un motif de traitement et ajoutez un commentaire si nécessaire.',
            'L\'alerte disparait de la liste une fois traitée.'
          ],
          result: 'Les alertes sont traitées au fil de l\'eau et ne s\'accumulent pas.',
          tips: ['Traitez les alertes au fur et a mesure de la journee plutot qu\'en fin de journee pour ne pas en oublier.']
        },
        {
          id: 'traiter-retard',
          title: 'Traiter un retard',
          description: 'Un retard est détecté lorsqu\'un employé pointe apres l\'heure de debut de son quart. Vous devez qualifier ce retard.',
          whenToUse: 'Lorsqu\'un employé arrive en retard et que vous devez documenter la raison.',
          steps: [
            'Localisez l\'alerte de retard dans les Actions prioritaires ou le module Alertes.',
            'Cliquez sur l\'alerte pour voir les détails : employé, heure prevue, heure reelle, duree du retard.',
            'Sélectionnez le motif : "Justifie" (transport, meteo, etc.) ou "Injustifie".',
            'Ajoutez un commentaire avec le contexte (ex: "Panne de bus signalée par l\'employé").',
            'Validez le traitement.'
          ],
          result: 'Le retard est documente et archive. Il apparaitra dans les statistiques de l\'employé.',
          warnings: ['Soyez objectif dans la qualification des retards. Les statistiques servent pour l\'evaluation des employés.']
        },
        {
          id: 'traiter-absence',
          title: 'Traiter une absence',
          description: 'Une absence est détectée lorsqu\'un employé planifie ne pointe pas du tout. Le traitement documente la raison de l\'absence.',
          whenToUse: 'En fin de journee ou le lendemain, lorsqu\'il est confirme que l\'employé n\'a pas travaille.',
          steps: [
            'Localisez l\'alerte d\'absence.',
            'Cliquez sur l\'alerte pour voir les détails.',
            'Si l\'employé a prevenu : sélectionnez "Conge", "Maladie" ou "Formation" selon le cas.',
            'Si l\'employé n\'a pas prevenu : sélectionnez "Injustifiee".',
            'Ajoutez la référence du justificatif si disponible.',
            'Validez.'
          ],
          result: 'L\'absence est qualifiée et tracee dans le dossier de l\'employé.',
          tips: ['Contactez l\'employé absent non prevenu avant de qualifier l\'absence comme "injustifiée". Il peut y avoir une urgence.']
        }
      ]
    },

    /* ============================================================
       4. VALIDATION DES POINTAGES
    ============================================================ */
    {
      id: 'validation-pointages',
      title: 'Validation des pointages',
      overview: 'Vérifiez et approuvez les heures de vos employés avant leur transfert en paie.',
      content: [
        {
          id: 'valider-journee',
          title: 'Valider une journee',
          description: 'Chaque jour, les pointages de vos employés doivent etre verifies et valides. La validation confirme que les heures enregistrees correspondent a la realite du travail effectué.',
          whenToUse: 'Quotidiennement, idealement le lendemain matin pour la journee précédente.',
          steps: [
            'Allez sur la page Rapports depuis le menu lateral.',
            'Sélectionnez la date de la journee a valider.',
            'Vérifiez chaque pointage : heures d\'arrivee et de depart coherentes avec la realite.',
            'Pour valider un pointage : cliquez sur la ligne, puis "Approuver" dans le panneau lateral.',
            'Pour valider toute la journee d\'un coup : cliquez sur "Valider la journee" en haut (sélectionné automatiquement tous les pointages en attente).',
            'Pour rejeter un pointage errone : cliquez sur "Rejeter" et saisissez un commentaire obligatoire.'
          ],
          screenshot: '/docs/reports-validation.png',
          screenshotAlt: 'Validation des pointages journaliers',
          result: 'Les pointages valides sont marques en vert. Ils sont prets pour le traitement en paie.',
          warnings: [
            'Ne validez pas aveuglément. Vérifiez que les heures correspondent a la realite.',
            'Un commentaire est obligatoire pour chaque rejet afin de garantir la traçabilité.'
          ]
        },
        {
          id: 'panneau-détail',
          title: 'Panneau de détail d\'un pointage',
          description: 'En cliquant sur une ligne de pointage, un panneau s\'ouvre a droite avec tous les détails : resume des heures, statut de validation, photos de pointage et timeline complete.',
          whenToUse: 'Pour examiner un pointage en détail avant de le valider ou le rejeter.',
          steps: [
            'Cliquez sur la ligne d\'un pointage dans le tableau des rapports.',
            'Le panneau lateral s\'ouvre avec 4 sections.',
            'Resume : Heure d\'entree, heure de sortie, duree totale, pauses.',
            'Validation : Statut actuel (en attente, approuve, rejete), nom du validateur et date si déjà traite.',
            'Photos : Photos prises au kiosque lors du pointage (si activees).',
            'Timeline : Liste chronologique de tous les evenements de la journee (entree, pauses, sortie).',
            'Utilisez les boutons "Approuver" ou "Rejeter" en bas du panneau.'
          ],
          result: 'Vous avez toutes les informations nécessaires pour prendre une decision de validation eclairee.'
        }
      ]
    },

    /* ============================================================
       5. ACTIONS GROUPEES
    ============================================================ */
    {
      id: 'actions-groupees',
      title: 'Actions groupees',
      overview: 'Gagnez du temps en traitant plusieurs éléments simultanement.',
      content: [
        {
          id: 'validation-masse',
          title: 'Validation en masse des pointages',
          description: 'Sélectionnez plusieurs pointages pour les approuver ou rejeter en une seule opération.',
          whenToUse: 'Lorsque la journee est standard et que tous les pointages sont corrects, ou pour rejeter un lot d\'anomalies.',
          steps: [
            'Sur la page Rapports, cochez les cases a gauche des pointages a traiter.',
            'La barre d\'actions en masse apparait en haut avec le nombre d\'éléments sélectionnés.',
            'Cliquez sur "Approuver" pour valider tous les pointages sélectionnés.',
            'Ou cliquez sur "Rejeter" pour rejeter (un commentaire global sera demande).',
            'Confirmez l\'action dans la boite de dialogue.'
          ],
          result: 'Tous les pointages sélectionnés sont traites en une seule opération.',
          tips: ['Utilisez "Tout sélectionner" pour sélectionner tous les pointages en attente de la journee, puis deselectionnez manuellement les cas particuliers a examiner individuellement.']
        },
        {
          id: 'traitement-masse-alertes',
          title: 'Traitement en masse des alertes',
          description: 'Traitez plusieurs alertes d\'un coup avec le meme motif, ideal pour les situations collectives.',
          whenToUse: 'Jour ferie, fermeture exceptionnelle, absence collective (panne de transport, intempéries).',
          steps: [
            'Dans la liste des alertes, cochez les alertes concernees.',
            'Cliquez sur "Traiter" dans la barre d\'actions groupees.',
            'Sélectionnez le motif commun (ex: "Jour ferie", "Absence collective").',
            'Ajoutez un commentaire explicatif.',
            'Confirmez le traitement.'
          ],
          result: 'Toutes les alertes selectionnees sont traitées avec le meme motif en une seule opération.',
          warnings: ['Vérifiez votre sélection avant de confirmer. Le traitement en masse s\'applique a tous les éléments coches.']
        }
      ]
    },

    /* ============================================================
       6. GESTION DE VOS EQUIPES
    ============================================================ */
    {
      id: 'gestion-équipes',
      title: 'Gestion de vos équipes',
      overview: 'Consultez les informations de vos employés, leurs plannings et leurs statistiques.',
      content: [
        {
          id: 'consulter-employés',
          title: 'Consulter vos employés',
          description: 'La page Employés vous donne accès a la liste de tous les employés de votre perimetre avec leurs informations et statistiques.',
          whenToUse: 'Pour vérifier les informations d\'un employé ou consulter ses statistiques.',
          steps: [
            'Cliquez sur "Employés" dans le menu lateral.',
            'La liste est automatiquement filtree par votre perimetre (zone/site).',
            'Utilisez la recherche pour trouver un employé par nom ou code.',
            'Cliquez sur un employé pour voir sa fiche détaillée dans le panneau lateral.',
            'La fiche montre : informations, affectation, statistiques de pointage.'
          ],
          result: 'Vous avez accès aux informations et statistiques de chaque employé de votre équipe.',
          tips: ['En tant que superviseur, vous pouvez consulter mais pas forcement modifier les informations des employés. Les modifications sont réservées aux roles RH et admin.']
        },
        {
          id: 'planning-équipe',
          title: 'Planning de votre équipe',
          description: 'Consultez la planification hebdomadaire de vos employés pour savoir qui est prevu chaque jour.',
          whenToUse: 'En debut de semaine pour anticiper les besoins et vérifier les affectations.',
          steps: [
            'Cliquez sur "Planification" dans le menu lateral.',
            'La vue calendrier affiche vos employés avec leurs quarts planifies pour la semaine.',
            'Chaque case coloree représente un quart assigne.',
            'Les cases vides indiquent que l\'employé n\'est pas planifie ce jour-la.',
            'Naviguez entre les semaines avec les fleches.'
          ],
          result: 'Vous visualisez la planification de votre équipe pour anticiper les opérations.'
        }
      ]
    },

    /* ============================================================
       7. RAPPORTS DE VOTRE PERIMETRE
    ============================================================ */
    {
      id: 'rapports-superviseur',
      title: 'Rapports',
      overview: 'Consultez les rapports de pointage et les statistiques de votre perimetre.',
      content: [
        {
          id: 'rapports-quotidiens',
          title: 'Rapports quotidiens',
          description: 'Les rapports journaliers montrent tous les pointages de vos employés pour une date donnee, avec les heures, durees et statuts de validation.',
          whenToUse: 'Quotidiennement pour vérifier et valider les heures de la veille.',
          steps: [
            'Allez sur "Rapports" dans le menu lateral.',
            'Sélectionnez la date souhaitee.',
            'Le tableau affiche les pointages de vos employés uniquement.',
            'Les KPIs en haut resument la journee : total, valides, en attente, rejetes.',
            'Exportez en Excel ou PDF si nécessaire.'
          ],
          result: 'Un rapport complet de la journee pour votre perimetre.'
        },
        {
          id: 'exporter-rapports',
          title: 'Exporter des rapports',
          description: 'Telechargez les rapports en Excel ou PDF pour les partager ou les archiver.',
          whenToUse: 'Pour envoyer un rapport a votre hierarchie, pour une reunion ou pour l\'archivage.',
          steps: [
            'Sur la page Rapports, appliquez les filtres souhaites (date, site, zone).',
            'Cliquez sur "Excel" pour un fichier tableur editable.',
            'Cliquez sur "PDF" pour un document formaté pour l\'impression.',
            'Le fichier se telecharge automatiquement.'
          ],
          result: 'Un fichier Excel ou PDF contenant les donnees du rapport actuel.',
          tips: ['L\'export respecte les filtres appliques : si vous filtrez par zone, seules les donnees de cette zone seront exportees.']
        }
      ]
    },

    /* ============================================================
       8. BONNES PRATIQUES TERRAIN
    ============================================================ */
    {
      id: 'bonnes-pratiques',
      title: 'Bonnes pratiques terrain',
      overview: 'Recommandations pour une gestion efficace de vos équipes avec PunchTM.',
      content: [
        {
          id: 'routine-quotidienne',
          title: 'Routine quotidienne recommandee',
          description: 'Une routine structurée vous permet de maintenir un suivi rigoureux et d\'anticiper les problemes.',
          whenToUse: 'Chaque jour de travail, adaptez selon votre rythme.',
          steps: [
            'MATIN (debut de quart) : Consultez le tableau de bord pour vérifier les presences.',
            'Identifiez les absents et retardataires. Contactez-les si nécessaire.',
            'Traitez les alertes de retard de la matinee avec les motifs adequats.',
            'MI-JOURNEE : Vérifiez que les employés en pause ont bien repris leur poste.',
            'Traitez les nouvelles alertes apparues.',
            'FIN DE JOURNEE : Validez les pointages de la journee dans les Rapports.',
            'Traitez toutes les alertes restantes.',
            'LENDEMAIN MATIN : Validez les pointages de la veille si non fait.',
            'Vérifiez le planning de la semaine a venir.'
          ],
          result: 'Un suivi rigoureux et proactif de votre équipe, avec zero alerte en attente.',
          tips: [
            'Ne laissez jamais les alertes s\'accumuler sur plusieurs jours. Traitez-les quotidiennement.',
            'Validez les pointages au plus tard le lendemain pour ne pas bloquer le processus de paie.'
          ]
        },
        {
          id: 'communication-équipe',
          title: 'Communication avec votre équipe',
          description: 'Bonnes pratiques pour informer et impliquer vos employés dans le systeme de pointage.',
          whenToUse: 'Lors du deploiement initial et en continu pour maintenir l\'adhesion.',
          steps: [
            'Expliquez a vos employés l\'importance du pointage : c\'est la base du calcul de leurs heures et de leur paie.',
            'Rappelez les regles : pointer a l\'arrivee, au depart, au debut et a la fin des pauses.',
            'Informez-les que les retards sont détectés automatiquement et documentes.',
            'Encouragez-les a signaler toute anomalie de pointage (oubli, erreur) rapidement.',
            'Soyez transparent sur le traitement des alertes : les decisions sont tracees et auditables.'
          ],
          result: 'Une équipe informee et impliquee, ce qui reduit les erreurs et ameliore la qualite des donnees.',
          warnings: ['Évitez de pointer pour un employé absent. C\'est considere comme une fraude et est détecté par le systeme.']
        },
        {
          id: 'gerer-anomalies',
          title: 'Gerer les anomalies courantes',
          description: 'Solutions aux situations fréquentes rencontrees sur le terrain.',
          whenToUse: 'Comme référence lorsque vous rencontrez une situation inhabituelle.',
          steps: [
            'EMPLOYE QUI OUBLIE DE POINTER : L\'absence sera signalee. Traitez l\'alerte avec le motif "Oubli de pointage" et contactez l\'employé.',
            'POINTAGE EN DOUBLE : Le systeme détecté les doublons. Rejetez le doublon dans les rapports.',
            'KIOSQUE EN PANNE : Notez les heures manuellement. Signalez la panne au service technique. Les pointages pourront etre saisis retroactivement par le RH.',
            'EMPLOYE NON RECONNU AU KIOSQUE : Vérifiez que son code est correct (5 chiffres). Vérifiez qu\'il est actif dans le systeme.',
            'MAUVAISE ZONE : Si un employé pointe sur un kiosque d\'une autre zone, le pointage est enregistre mais signale. Corrigez l\'affectation si nécessaire.'
          ],
          result: 'Vous savez gerer les situations courantes sans avoir besoin de contacter le support.'
        }
      ]
    }
  ]
};
